/**
 * UI-6c Task 7 — CsatConfigPage.
 *
 * Tenant-admin page for managing `csat_configs`. Composition:
 *
 *   - The list is fetched with `useAdminResource('/admin/csat/configs',
 *     { responseKey: 'configs' })`. Backend returns `{ configs: [...] }`.
 *   - Create + Edit go through `CsatConfigDrawer` (the drawer owns the save
 *     API call and reports back via `onSaved`). The page never uses the
 *     hook's `create`/`update`.
 *   - Row Delete opens a `confirmDialog` whose message names the config and
 *     warns that "active surveys using this config will stop after the next
 *     deployment". Accepting issues `DELETE /admin/csat/configs/:id` then
 *     refetches.
 *
 * Backend contract (apps/api/src/routes/admin/csat/):
 *   GET    /admin/csat/configs      -> { configs: CsatConfig[] }
 *   POST   /admin/csat/configs      -> 201 + row (drawer owns)
 *   PATCH  /admin/csat/configs/:id  -> 200 + row (drawer owns)
 *   DELETE /admin/csat/configs/:id  -> 204
 *
 * Permission: `csat:admin`.
 */
import { useState, type JSX } from 'react'
import { Button } from 'primereact/button'
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog'
import { Tag } from 'primereact/tag'

import PageHeader from '../../components/admin/PageHeader.js'
import ResourceTable, {
  type ColumnDef,
} from '../../components/admin/ResourceTable.js'
import RequirePermission from '../../components/auth/RequirePermission.js'
import {
  CsatConfigDrawer,
  type CsatConfig,
} from '../../components/admin/csat/CsatConfigDrawer.js'
import { useAdminResource } from '../../hooks/useAdminApi.js'
import { api } from '../../api/client.js'
import { useToast } from '../../components/system/ToastProvider.js'
import { formatDateTime } from '../../utils/formatDate.js'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type TagSeverity = 'success' | 'info' | 'warning' | 'danger' | undefined

/**
 * Trigger labels are short + stable so the column scans cleanly. Severity
 * colors are arbitrary — they exist mostly to keep the tag visually
 * distinguishable from the surrounding text, not to encode meaning.
 */
function triggerLabel(trigger: CsatConfig['trigger']): string {
  switch (trigger) {
    case 'post_call':
      return 'Post-Call'
    case 'post_conversation':
      return 'Post-Conversation'
    case 'both':
      return 'Both'
  }
}

function triggerSeverity(trigger: CsatConfig['trigger']): TagSeverity {
  switch (trigger) {
    case 'post_call':
      return 'info'
    case 'post_conversation':
      return 'success'
    case 'both':
      return 'warning'
  }
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function CsatConfigPage(): JSX.Element {
  const toast = useToast()

  const {
    items: configs,
    loading,
    refetch,
    pagination,
  } = useAdminResource<CsatConfig>('/admin/csat/configs', {
    responseKey: 'configs',
  })

  // --- Drawer state --------------------------------------------------------
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editing, setEditing] = useState<CsatConfig | null>(null)

  // --- Delete flow ---------------------------------------------------------
  function handleDelete(config: CsatConfig): void {
    confirmDialog({
      message: `Delete CSAT config "${config.name}"? Active surveys using this config will stop after the next deployment.`,
      header: 'Delete CSAT Config',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      // try/catch wraps the whole body so a rejecting api.delete can never
      // escape as an unhandled rejection — failures surface as a toast.
      accept: async () => {
        try {
          await api.delete(`/admin/csat/configs/${config.id}`)
          toast.success('CSAT config deleted')
          refetch()
        } catch (e) {
          toast.error(
            e instanceof Error ? e.message : 'Failed to delete CSAT config',
          )
        }
      },
    })
  }

  // --- Cell renderers ------------------------------------------------------

  function triggerCell(c: CsatConfig): JSX.Element {
    return <Tag value={triggerLabel(c.trigger)} severity={triggerSeverity(c.trigger)} />
  }

  function questionsCountCell(c: CsatConfig): string {
    // `??` guards against an unexpectedly missing array in the API payload.
    return String(c.questions?.length ?? 0)
  }

  function activeCell(c: CsatConfig): JSX.Element {
    return (
      <span
        className={
          c.isActive
            ? 'inline-flex items-center rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700'
            : 'inline-flex items-center rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600'
        }
        aria-label={c.isActive ? 'Active' : 'Inactive'}
      >
        {c.isActive ? 'Yes' : 'No'}
      </span>
    )
  }

  function updatedCell(c: CsatConfig): string {
    return formatDateTime(c.updatedAt)
  }

  const columns: ColumnDef<CsatConfig>[] = [
    { label: 'Name', accessor: 'name' },
    { label: 'Trigger', accessor: (c) => triggerCell(c) },
    { label: 'Questions', accessor: (c) => questionsCountCell(c) },
    { label: 'Active', accessor: (c) => activeCell(c) },
    { label: 'Updated', accessor: (c) => updatedCell(c) },
  ]

  return (
    <RequirePermission permission="csat:admin">
      <div className="space-y-4 p-4">
        <PageHeader
          title="CSAT Surveys"
          description="Configure when CSAT surveys fire and what questions appear."
          actions={
            <Button
              label="+ New CSAT Config"
              onClick={() => {
                setEditing(null)
                setDrawerOpen(true)
              }}
            />
          }
        />

        <ResourceTable<CsatConfig>
          columns={columns}
          rows={configs}
          loading={loading}
          actions={['edit', 'delete']}
          onEdit={(c) => {
            setEditing(c)
            setDrawerOpen(true)
          }}
          onDelete={handleDelete}
          empty={{
            title: 'No CSAT configs',
            description: 'Create one to start dispatching CSAT surveys to customers.',
            icon: 'pi pi-comments',
          }}
          pagination={pagination}
        />

        <CsatConfigDrawer
          open={drawerOpen}
          config={editing}
          onClose={() => setDrawerOpen(false)}
          onSaved={() => {
            refetch()
            setDrawerOpen(false)
          }}
        />

        <ConfirmDialog />
      </div>
    </RequirePermission>
  )
}
